import { useCallback, useMemo, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useWorldCupMatchesQuery } from '../../../src/worldcup/hooks/useWorldCupMatchesQuery';
import { worldCupQueryKeys } from '../../../src/worldcup/constants/worldcupQueryKeys';
import { useCompetitionsListQuery } from '../../../src/competitions/hooks/useCompetitionsListQuery';
import { useCompetitionBasePath } from '../../../src/competitions/contexts/CompetitionContext';
import {
  competitionMatchRoute,
  competitionMatchStatsRoute,
} from '../../../src/competitions/helpers/competitionRoutes.helper';
import { useSeasonPickSaver } from '../../../src/competitions/hooks/useSeasonPickSaver';
import {
  buildStageSections,
  type StageSection,
} from '../../../src/competitions/helpers/tournamentStageSections.helper';
import { defaultMatchweekRound } from '../../../src/competitions/helpers/matchweekSections.helper';
import type { ApiMatch } from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import { useTournamentGoldenBall } from '../../../src/competitions/hooks/useTournamentGoldenBall';
import { resolveGoldenBallSlot } from '../../../src/competitions/helpers/tournamentGoldenBall.helper';
import { buildKnockoutTies, aggregateForMatch } from '../../../src/competitions/helpers/knockoutTies.helper';
import { bracketStages } from '../../../src/competitions/helpers/tournamentBracket.helper';
import { SeasonPicksView } from '../SeasonPicks/SeasonPicksView';
import { CompetitionFutures } from '../CompetitionFutures/CompetitionFutures';
import type { SeasonPickValue, SeasonPickBoost } from '../SeasonPicks/SeasonMatchPickCard';
import { TOURNAMENT_PICKS_COPY, TOURNAMENT_PICKS_FALLBACK_TITLE } from './TournamentPicks.constants';

/**
 * Tournament picks container (specs/champions-league, Story 2).
 *
 * Same data path as the season screen — the matches query and pick saver are both already
 * parameterized by competition — but grouped by STAGE instead of matchweek. Sections come
 * entirely from server-supplied `stage`/`stageLabel`/`round`/`displayOrder`, so a new
 * tournament needs no change here.
 *
 * Renders through SeasonPicksView, which is shared by both formats and takes its
 * section-flavour copy and chip label as props.
 */
export function TournamentPicksScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { competitionId: rawCompetitionId } = useLocalSearchParams<{ competitionId: string }>();
  const competitionId = rawCompetitionId ?? '';

  const basePath = useCompetitionBasePath();

  const { data, isLoading, isError } = useWorldCupMatchesQuery(competitionId, competitionId !== '');
  const { data: competitionsList } = useCompetitionsListQuery();

  const [localPicks, setLocalPicks] = useState<Record<string, SeasonPickValue>>({});
  const [selectedRoundState, setSelectedRoundState] = useState<string | null>(null);

  const onSaveError = useCallback((matchId: string) => {
    setLocalPicks((prev) => {
      if (!(matchId in prev)) return prev;
      const next = { ...prev };
      delete next[matchId];
      return next;
    });
  }, []);
  const { schedulePickSave, saveErrorMatchIds } = useSeasonPickSaver({ competitionId, onSaveError });

  const sections: StageSection<ApiMatch>[] = useMemo(
    () => buildStageSections(data?.matches ?? []),
    [data?.matches],
  );
  // Sections are Matchweek-shaped, so the season default-selection rule applies unchanged:
  // the section holding the next kickoff, or the most recent one when all have finished.
  const selectedRound = selectedRoundState ?? defaultMatchweekRound(sections);

  const competition = competitionsList?.find((c) => c.id === competitionId);
  const title = competition?.brand?.name ?? competition?.name ?? TOURNAMENT_PICKS_FALLBACK_TITLE;

  // A knockout tie is two fixtures and one outcome, so the second leg states the aggregate
  // (AC-6.9). Built once for the screen rather than per card: pairing legs is a question about
  // the WHOLE fixture list, and asking it per card would be quadratic.
  const knockoutTies = useMemo(
    () => buildKnockoutTies(
      data?.matches ?? [],
      // Which stages are knockout rounds is the competition's own answer (AC-4.5), not a stage
      // name this screen knows: a stage holding many rounds is a table phase.
      bracketStages(
        competition?.rules?.stageOrder ?? [],
        competition?.rules?.multiRoundStages ?? {},
      ),
    ),
    [data?.matches, competition?.rules?.stageOrder, competition?.rules?.multiRoundStages],
  );
  const resolveAggregate = useCallback(
    (match: ApiMatch) => aggregateForMatch(match, knockoutTies),
    [knockoutTies],
  );

  const resolvePick = useCallback(
    (match: ApiMatch): SeasonPickValue | null => {
      const local = localPicks[match.matchId];
      if (local) return local;
      if (match.prediction) return { home: match.prediction.home, away: match.prediction.away };
      return null;
    },
    [localPicks],
  );

  const onChangePick = useCallback(
    (matchId: string, home: number, away: number) => {
      setLocalPicks((prev) => ({ ...prev, [matchId]: { home, away } }));
      schedulePickSave(matchId, home, away);
    },
    [schedulePickSave],
  );

  const onRetry = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: worldCupQueryKeys.matchesStatic(competitionId) });
    queryClient.invalidateQueries({ queryKey: worldCupQueryKeys.matchesMe(competitionId) });
  }, [queryClient, competitionId]);

  const onBack = useCallback(() => {
    if (router.canGoBack()) router.back();
    else router.replace('/app/competitions');
  }, [router]);

  // Story 10: the same two card actions the World Cup has. Built from this competition's
  // basePath, so they land in this tree rather than teleporting into the WC stack.
  const onStatsPress = useCallback(
    (matchId: string) => {
      router.push(competitionMatchStatsRoute(basePath, matchId) as never);
    },
    [router, basePath],
  );

  const onFixturePress = useCallback(
    (matchId: string) => {
      router.push(competitionMatchRoute(basePath, matchId) as never);
    },
    [router, basePath],
  );

  const renderChipLabel = useCallback(
    (section: { round: string }) =>
      sections.find((s) => s.round === section.round)?.shortLabel ?? section.round,
    [sections],
  );

  const { toggleBoost, isBoosted, isSlotTakenByOtherMatch } = useTournamentGoldenBall({
    competitionId,
    rules: competition?.rules,
    matches: data?.matches,
  });

  const resolveBoost = useCallback(
    (match: ApiMatch): SeasonPickBoost | null => {
      const slot = resolveGoldenBallSlot(match, competition?.rules);
      if (!slot.eligible) return null;
      return {
        eligible: true,
        active: isBoosted(match),
        placeable: slot.placeable,
        lockedByOther: isSlotTakenByOtherMatch(match),
        onToggle: toggleBoost,
      };
    },
    [competition?.rules, isBoosted, isSlotTakenByOtherMatch, toggleBoost],
  );

  // Winner and top scorer, for a competition whose server says it takes them (AC-15.1). The
  // options, the viewer's pick and the lock all ride on the matches payload this screen
  // already holds, so this costs one extra query (the aggregate percentages) and no new state.
  const futuresSlot = competition?.rules?.futures?.enabled
    ? (
      <CompetitionFutures
        competitionId={competitionId}
        futures={data?.futures ?? null}
        isEditable={data?.futuresEditable ?? false}
        futuresOptions={data?.futuresOptions ?? null}
      />
    )
    : null;

  return (
    <SeasonPicksView
      title={title}
      matchweeks={sections}
      selectedRound={selectedRound}
      onSelectRound={setSelectedRoundState}
      resolvePick={resolvePick}
      resolveAggregate={resolveAggregate}
      saveErrorMatchIds={saveErrorMatchIds}
      onChangePick={onChangePick}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      onBack={onBack}
      showHeader={false}
      copy={TOURNAMENT_PICKS_COPY}
      renderChipLabel={renderChipLabel}
      resolveBoost={resolveBoost}
      onStatsPress={onStatsPress}
      onFixturePress={onFixturePress}
      futuresSlot={futuresSlot}
    />
  );
}
